import { generateQueryString, getDefectAbbreviation } from 'common/utils';
import { getDefectDisplayName } from 'common/utils/defectDisplayName';
import { history } from 'common/helpers';
import { store } from 'app/store';
import { machinesActions } from 'home/Machines/machines.actions';
import i18n from '../../../i18n/config';

// Name portion of a `<display name> (<count>)` chart tooltip.
const defectNameFromTooltip = tooltip =>
  (tooltip || '').replace(/\s*\(\d+\)\s*$/, '').trim();

const countFromChartClick = (data) => {
  if (data.value != null && !Number.isNaN(Number(data.value))) return Number(data.value);
  const countMatch = (data.tooltip || '').match(/\((\d+)\)\s*$/);
  return countMatch ? Number(countMatch[1]) : null;
};

// A chart row carries only the abbreviated label and the count, so the defect id
// has to be reconstructed. Abbreviations collide ("Bearing Wear" and "Belt Wear"
// both give BW) and equal counts are common, so narrow an ambiguous match by the
// tooltip name before accepting it, and return nothing rather than drill into a
// defect the user did not click.
const findDefectFromChartClick = (data, rawData) => {
  if (!data || !Array.isArray(rawData) || data.label == null) return null;

  const count = countFromChartClick(data);
  if (count == null) return null;

  const candidates = rawData.filter(
    defect => getDefectAbbreviation(defect.name) === data.label && Number(defect.count) === count
  );
  if (candidates.length === 1) return candidates[0];
  if (candidates.length < 2) return null;

  // Tooltips are localized, so compare against both the rendered display name
  // (derived from the id) and the raw API name.
  const clickedName = defectNameFromTooltip(data.tooltip);
  if (!clickedName) return null;
  const exact = candidates.filter((defect) => {
    const displayName = getDefectDisplayName(i18n.t.bind(i18n), {
      defectId: defect.id,
      defectName: defect.name,
    });
    return displayName === clickedName || defect.name === clickedName;
  });
  return exact.length === 1 ? exact[0] : null;
};

const clearMachinesRequiringAttentionFilter = () => {
  const pathname = history && history.location && history.location.pathname;
  if (pathname === '/assets') return;
  if (
    store &&
    store.getState &&
    store.getState().machines &&
    store.getState().machines.machinesRequiringAttention &&
    store.getState().machines.machinesRequiringAttention.show
  ) {
    store.dispatch(machinesActions.setMachinesRequiringAttention(false, false));
  }
};

const getAccountId = (filterOptions) => {
  // Useful for widgets not present in admindashboard
  let accountId = filterOptions && filterOptions.account_id;
  if (!accountId && Array.isArray(filterOptions)) {
    const accountFilter = filterOptions.find(filter => filter.name === 'account_id');
    if (accountFilter) {
      accountId = Array.isArray(accountFilter.value) ? accountFilter.value[0] : accountFilter.value;
    }
  }
  if (!accountId && store && store.getState) {
    const stateFromStore = store.getState();
    if (stateFromStore && stateFromStore.user && stateFromStore.user.user) {
      accountId = stateFromStore.user.user.account_id;
    }
  }
  return accountId;
};

// When already on /assets (e.g. the Insights detail chart), merge the clicked
// filter into the current URL params so existing filters survive and the page
// tracks it; otherwise navigate in fresh.
const pushAssetsFilter = (route, filterStr) => {
  const onAssets = history && history.location && history.location.pathname === route;
  if (onAssets) {
    const merged = new URLSearchParams(history.location.search);
    new URLSearchParams(filterStr).forEach((value, key) => merged.set(key, value));
    history.replace({ pathname: route, search: merged.toString() });
  } else {
    history.push({ pathname: route, search: filterStr });
  }
};

export const navToAssetsPage = (widget_name, data, filterOptions = null, filterName = null, swithAccount = () => {}) => {
  const route = '/assets';
  clearMachinesRequiringAttentionFilter();
  let filter;
  if (widget_name === 'incomplete_asset_setup_breakdown' || widget_name === 'open_events_breakdown') {
    filter = [filterOptions.filter(
      item => !item.groupName
    )[data]];
    filter = generateQueryString({ [filterName]: filter });
  } else if (widget_name === 'asset_health_scores_breakdown' || widget_name === 'vfd_breakdown') {
    filter = [filterOptions[data]];
    filter = generateQueryString({ [filterName]: filter });
  } else if (widget_name === 'plant_asset_health') {
    filter = generateQueryString({ condition: [data.text] });
  } else if (widget_name === 'current_plant_utilization') {
    filter = generateQueryString({ state: [data.text] });
  } else if (widget_name === 'alarm_review_and_action_report') {
    let account_id = null;
    filter = generateQueryString({ alarm_metrics: [data] });
    filterOptions.forEach((filter) => {
      if (filter.name === 'account_id') {
        account_id = filter.value;
      }
    });
    if (account_id) {
      swithAccount(account_id, () => {
        history.push({
          pathname: route,
          search: filter
        });
      });
    }
  }
  pushAssetsFilter(route, filter);
};

export const navToDevicesPage = (widget_name, data, filterOptions, switchAccount = () => {}, isAdminDashboard = false) => {
  const route = '/devices';
  let filter;
  if (widget_name === 'firmware_version_breakdown') {
    if (data.text === 'Other' || data.label === 'Other') {
      filter = '';
    } else {
      filter = generateQueryString({ firmwares: [data.text] });
    }
    history.push({
      pathname: route,
      search: filter
    });
    return;
  }
  if (widget_name === 'firmware') {
    filter = generateQueryString({ [filterOptions[data].name]: [data.text] });
  } else if (widget_name === 'device_unreachable_above_fourteen_days_count'
    || widget_name === 'devices_with_continuous_outliers_count'
    || widget_name === 'devices_with_inconsistent_vibration_levels') {
    if (isAdminDashboard) {
      const accountFilter = filterOptions.find(filter => filter.name === 'account_id');

      if (!accountFilter || !accountFilter.value || accountFilter.value.length !== 1) {
        return;
      }
    }

    if (widget_name === 'devices_with_continuous_outliers_count') {
      filterOptions.push({ name: 'other_filters', op: 'in', value: ['devices_with_continuous_outliers'] });
      filter = generateQueryString({ other_filters: ['devices_with_continuous_outliers'] });
    } else if (widget_name === 'device_unreachable_above_fourteen_days_count') {
      filterOptions.push({ name: 'last_measurement', op: 'in', value: ['unreachable_extended'] });
      filter = generateQueryString({ last_measurement: ['unreachable_extended'] });
    } else if (widget_name === 'devices_with_inconsistent_vibration_levels') {
      filterOptions.push({ name: 'other_filters', op: 'in', value: ['devices_with_inconsistent_vibrations'] });
      filter = generateQueryString({ other_filters: ['devices_with_inconsistent_vibrations'] });
    }

    let account_id = null;
    filterOptions.forEach((filter) => {
      if (filter.name === 'account_id') {
        account_id = filter.value[0];
      }
    });

    if (account_id) {
      switchAccount(account_id, () => {
        history.push({
          pathname: route,
          search: filter
        });
      });
      return;
    }
  } else {
    filter = generateQueryString({ [filterOptions[data].name]: filterOptions[data].value });
  }

  history.push({
    pathname: route,
    search: filter
  });
};

// Activity-list filters live per account in localStorage and are read by the
// Activity page on mount. Exported so an org rollup can seed the same shape its
// site-level twin writes, instead of restating it and drifting.
export const seedActivityListFilters = (accountId, filters) => {
  let stored = JSON.parse(localStorage.getItem('activityListFilters'));
  if (!stored || typeof stored !== 'object') {
    stored = {};
  }
  localStorage.setItem(
    'activityListFilters',
    JSON.stringify({ ...stored, [accountId]: filters })
  );
};

// Tasks that saved money, over all time and every state — matches what the
// total_cost_saved widget aggregates.
export const costSavedActivityFilters = () => ({
  search_key: '',
  type: ['task'],
  has_cost_saved: true,
  days: { value: null, text: i18n.t('activity.options.days.all') },
  state: ['open', 'in_progress', 'scheduled', 'review_pending', 'closed'],
  event_type: [],
  machines: [],
  read_status: [],
  analysis: [],
  suspension_status: [],
  defect: []
});

export const unreadEventsActivityFilters = () => ({
  type: ['event'],
  read_status: ['unread']
});

// Org rows click through whole, so these mirror each site-level widget's full
// filter. transient because ActivityTabHeader reads the same entry to scope the
// unread badge — a filter left behind would narrow that too.

export const openEventsActivityFilters = () => ({
  type: ['event'],
  transient: true,
  state: ['open', 'in_progress', 'scheduled', 'review_pending']
});

// Closed too: the row counts total_tasks.
export const tasksActivityFilters = () => ({
  type: ['task'],
  transient: true,
  state: ['open', 'in_progress', 'scheduled', 'review_pending', 'closed']
});

export const defectsActivityFilters = () => ({
  type: ['task'],
  has_defect: true,
  transient: true,
  state: ['open', 'in_progress', 'scheduled', 'review_pending', 'closed']
});

export const navToActiviyPage = (widget_name, data, filterOptions, swithAccount = () => {}) => {
  if (data.tooltip === 'Others') return;

  const route = '/activity';
  let activityListFilters = JSON.parse(localStorage.getItem('activityListFilters'));
  if (!activityListFilters || typeof activityListFilters !== 'object') {
    activityListFilters = {};
  }

  if (widget_name === 'not_analysed_open_alarms') {
    let account_id = null;
    if (filterOptions && Array.isArray(filterOptions)) {
      filterOptions.forEach((filter) => {
        if (filter.name === 'account_id') {
          account_id = filter.value;
        }
      });
    }

    if (account_id) {
      activityListFilters = {
        ...activityListFilters,
        [account_id]: {
          type: ['event'],
          analysis: [data]
        }
      };
      localStorage.setItem('activityListFilters', JSON.stringify(activityListFilters));
      swithAccount(account_id, () => {
        history.push({
          pathname: route,
        });
      });
    } else if (!account_id) {
      account_id = getAccountId(filterOptions);
      activityListFilters = {
        ...activityListFilters,
        [account_id]: {
          type: ['event'],
          analysis: [data]
        }
      };
      localStorage.setItem('activityListFilters', JSON.stringify(activityListFilters));
      history.push({
        pathname: route,
      });
    }
  } else if (widget_name === 'unread_events') {
    seedActivityListFilters(getAccountId(filterOptions), unreadEventsActivityFilters());
    history.push({
      pathname: route,
    });
  } else if (widget_name === 'total_cost_saved') {
    const accountId = getAccountId(filterOptions);

    if (!accountId) {
      history.push({ pathname: route });
      return;
    }

    seedActivityListFilters(accountId, costSavedActivityFilters());
    if (swithAccount && accountId) {
      swithAccount(accountId, () => {
        history.push({ pathname: route });
      });
    } else {
      history.push({ pathname: route });
    }
  } else if (widget_name === 'open_closed_events') {
    const state = data === 'closed' ? ['closed'] : ['open', 'in_progress', 'scheduled', 'review_pending'];
    const accountId = getAccountId(filterOptions);

    if (!accountId) {
      history.push({
        pathname: route,
      });
      return;
    }

    activityListFilters = {
      ...activityListFilters,
      [accountId]: {
        type: ['event'],
        state
      }
    };
    localStorage.setItem('activityListFilters', JSON.stringify(activityListFilters));
    history.push({
      pathname: route,
    });
  } else if (widget_name === 'diagnostic_rule_result_count') {
    const state = data === 'review_pending' ? ['review_pending'] : ['open', 'in_progress'];
    const accountId = getAccountId(filterOptions);

    if (!accountId) {
      history.push({
        pathname: route,
      });
      return;
    }

    activityListFilters = {
      ...activityListFilters,
      [accountId]: {
        type: ['diagnosis'],
        state
      }
    };
    localStorage.setItem('activityListFilters', JSON.stringify(activityListFilters));
    history.push({
      pathname: route,
    });
  } else if (widget_name === 'open_closed_tasks') {
    const state = data === 'completed' ? ['closed'] : ['open', 'in_progress', 'scheduled', 'review_pending'];
    const accountId = getAccountId(filterOptions);

    if (!accountId) {
      history.push({
        pathname: route,
      });
      return;
    }

    activityListFilters = {
      ...activityListFilters,
      [accountId]: {
        type: ['task'],
        state
      }
    };
    localStorage.setItem('activityListFilters', JSON.stringify(activityListFilters));
    history.push({
      pathname: route,
    });
  } else if (widget_name === 'open_closed_repairs') {
    const state = data === 'completed' ? ['closed'] : ['open', 'in_progress', 'scheduled', 'review_pending'];
    const accountId = getAccountId(filterOptions);

    const repairsFilter = {
      type: ['repair'],
      state
    };

    if (!accountId) {
      history.push({
        pathname: route,
      });
      return;
    }

    activityListFilters = {
      ...activityListFilters,
      [accountId]: repairsFilter
    };
    localStorage.setItem('activityListFilters', JSON.stringify(activityListFilters));
    history.push({
      pathname: route,
    });
  } else if (widget_name === 'total_defects_detected' || widget_name === 'active_defects_detected' || widget_name === 'defects_summary') {
    // defects_summary belongs to the asset dashboard, but navToMachineActivityPage
    // delegates here whenever machineId is missing, so it must match in both.
    const defectFilter = { type: ['task'], has_defect: true, transient: true };
    if (widget_name === 'active_defects_detected') {
      defectFilter.state = ['open', 'in_progress', 'scheduled', 'review_pending'];
    } else if (widget_name === 'total_defects_detected' || widget_name === 'defects_summary') {
      defectFilter.state = ['open', 'in_progress', 'scheduled', 'review_pending', 'closed'];
    }

    if (data && filterOptions && filterOptions.rawData) {
      const defect = findDefectFromChartClick(data, filterOptions.rawData);
      const defect_id = defect && defect.id;
      if (defect_id) {
        defectFilter.defect = [defect_id];
      }
    }

    const targetAccountId = (filterOptions && filterOptions.account_id) || getAccountId(filterOptions);
    if (targetAccountId) {
      activityListFilters = {
        ...activityListFilters,
        [targetAccountId]: defectFilter
      };
      localStorage.setItem('activityListFilters', JSON.stringify(activityListFilters));
    }
    history.push({
      pathname: route,
    });
  } else {
    history.push({
      pathname: route,
    });
  }
};

export const navToMachineActivityPage = (widget_name, data, filterOptions, machineId) => {
  if (!machineId) {
    navToActiviyPage(widget_name, data, filterOptions);
    return;
  }

  const route = `/machines/${machineId}/activity`;
  let machineActivityFilters = JSON.parse(localStorage.getItem('machineActivityListFilters')) || {};
  // Scoped to the machine as well as the account: Activity.js only clears a
  // consumed filter on its success path, so an abandoned click would otherwise
  // still be sitting there the next time any other asset's list is opened.
  const accountId = getAccountId(filterOptions);
  // Keyed by machine as well as account: Activity.js only clears a consumed
  // filter on its success path, so an abandoned click would otherwise still be
  // sitting there the next time any other asset's activity list is opened.
  const filterKey = `${accountId}:${machineId}`;

  if (filterOptions && filterOptions.type && filterOptions.type.includes('event')) {
    if (accountId) {
      const eventFilters = {
        type: ['event'],
        state: ['open', 'in_progress', 'scheduled', 'review_pending']
      };
      // Add analysis filter if specified
      if (filterOptions.analysis && filterOptions.analysis.length > 0) {
        eventFilters.analysis = filterOptions.analysis;
      }
      machineActivityFilters = {
        ...machineActivityFilters,
        [filterKey]: eventFilters
      };
      localStorage.setItem('machineActivityListFilters', JSON.stringify(machineActivityFilters));
    }
    history.push({ pathname: route });
    return;
  }

  // Handle diagnosis type filter
  if (filterOptions && filterOptions.type && filterOptions.type.includes('diagnosis')) {
    if (accountId) {
      const diagnosisFilters = {
        type: ['diagnosis'],
        state: filterOptions.state || ['open', 'in_progress']
      };
      machineActivityFilters = {
        ...machineActivityFilters,
        [filterKey]: diagnosisFilters
      };
      localStorage.setItem('machineActivityListFilters', JSON.stringify(machineActivityFilters));
    }
    history.push({ pathname: route });
    return;
  }

  if (!accountId) {
    history.push({ pathname: route });
    return;
  }

  // The asset dashboard names its defect widget `defects_summary`; the site dashboard
  // uses total_/active_defects_detected. All three drill into the same filtered list.
  const isDefectWidget = widget_name === 'total_defects_detected'
    || widget_name === 'active_defects_detected'
    || widget_name === 'defects_summary';
  if (isDefectWidget) {
    const defectFilter = { type: ['task'], has_defect: true, transient: true };
    if (widget_name === 'active_defects_detected') {
      defectFilter.state = ['open', 'in_progress', 'scheduled', 'review_pending'];
    } else if (widget_name === 'total_defects_detected' || widget_name === 'defects_summary') {
      // Both list closed defects, so keep them in scope. Named explicitly: a new
      // defect widget should be an obvious omission here, not silently closed-inclusive.
      defectFilter.state = ['open', 'in_progress', 'scheduled', 'review_pending', 'closed'];
    }

    if (data && filterOptions && filterOptions.rawData) {
      const defect = findDefectFromChartClick(data, filterOptions.rawData);
      const defect_id = defect && defect.id;
      if (defect_id) {
        defectFilter.defect = [defect_id];
      }
    }

    machineActivityFilters = {
      ...machineActivityFilters,
      [filterKey]: defectFilter
    };
    localStorage.setItem('machineActivityListFilters', JSON.stringify(machineActivityFilters));
    history.push({ pathname: route });
    return;
  }

  history.push({ pathname: route });
};
